import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { STEP_UP_TOKEN_TTL_MS } from "../../application/hooks/useStepUpVerification";
import { useSignInMethodChange } from "../../application/hooks/useSignInMethodChange";
import { useAuthenticatorSetup } from "../../application/hooks/useAuthenticatorSetup";
import { AUTH_FLOW_PURPOSE_RESET_TOTP } from "../../domain/constants/authFlowConstants";
import { OTP_PREFERENCE_EMAIL } from "../../domain/constants/otpConstants";
import type { QrCode } from "../../domain/entities/QrCode";
import { AuthenticatorSetupStep } from "./AuthenticatorSetupStep";
import { PasswordField } from "./PasswordField";
import { VerifyIdentityStep } from "./VerifyIdentityStep";

type Step = "currentPassword" | "verify" | "loadFailed" | "authenticatorSetup";

/** Set up a first authenticator, or replace the existing one. */
export type AuthenticatorDialogMode = "setup" | "reconfigure";

// Setup: password → QR. Reconfigure adds the email code (and reset) in between.
const STEP_NUMBERS: Record<AuthenticatorDialogMode, Record<Step, number>> = {
  setup: { currentPassword: 1, verify: 1, loadFailed: 1, authenticatorSetup: 2 },
  reconfigure: { currentPassword: 1, verify: 2, loadFailed: 2, authenticatorSetup: 3 },
};

const errorMessage = (err: unknown, fallback: string) =>
  err instanceof Error && err.message ? err.message : fallback;

interface Props {
  mode: AuthenticatorDialogMode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string | null;
  /** Called when the old authenticator is removed and when the new one is set up. */
  onAuthenticatorChanged: () => void;
}

/**
 * Authenticator setup from Settings, in the same shape as changing the
 * sign-in method:
 * - setup: current password → scan QR and confirm a code.
 * - reconfigure: current password → email code (RESET_TOTP only accepts
 *   email) → reset-totp with that temp token → scan the new QR and confirm.
 */
export const AuthenticatorDialog: React.FC<Props> = ({
  mode,
  open,
  onOpenChange,
  email,
  onAuthenticatorChanged,
}) => {
  const { t } = useTranslation("settings");
  const { verifyCurrentPassword, loading: checkingPassword } = useSignInMethodChange();
  const {
    resetAuthenticator,
    startAuthenticatorSetup,
    loading: settingUp,
  } = useAuthenticatorSetup();
  const loading = checkingPassword || settingUp;

  const isReconfigure = mode === "reconfigure";
  const copyKey = isReconfigure ? "reconfigureDialog" : "setupAuthenticatorDialog";
  const stepNumbers = STEP_NUMBERS[mode];
  const totalSteps = isReconfigure ? 3 : 2;

  const [step, setStep] = useState<Step>("currentPassword");
  const [currentPassword, setCurrentPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [tempTokenExpiresAt, setTempTokenExpiresAt] = useState(0);
  const [verifyNotice, setVerifyNotice] = useState<string | null>(null);
  const [resetDone, setResetDone] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<QrCode | null>(null);

  const reset = () => {
    setStep("currentPassword");
    setCurrentPassword("");
    setPasswordError(null);
    setTempToken(null);
    setTempTokenExpiresAt(0);
    setVerifyNotice(null);
    setResetDone(false);
    setLoadError(null);
    setQrCode(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (loading) return;
    if (!next) reset();
    onOpenChange(next);
  };

  // Reconfigure removes the old authenticator first (once); both modes then
  // fetch the QR for the new one.
  const loadQr = async (token: string | null) => {
    try {
      if (isReconfigure && !resetDone) {
        await resetAuthenticator(token ?? "");
        setResetDone(true);
        onAuthenticatorChanged();
      }
      setQrCode(await startAuthenticatorSetup());
      setStep("authenticatorSetup");
    } catch (err) {
      setLoadError(
        errorMessage(
          err,
          isReconfigure ? t("reconfigureDialog.resetError") : t("verifyIdentity.setupLoadError"),
        ),
      );
      setStep("loadFailed");
    }
  };

  const handleCurrentPasswordNext = async () => {
    if (!currentPassword) {
      setPasswordError(t("reconfigureDialog.currentPasswordRequired"));
      return;
    }
    try {
      await verifyCurrentPassword(currentPassword);
    } catch (err) {
      setPasswordError(errorMessage(err, t("reconfigureDialog.verifyPasswordError")));
      return;
    }

    if (isReconfigure) {
      setVerifyNotice(null);
      setStep("verify");
    } else {
      await loadQr(null);
    }
  };

  const handleVerified = async (token: string) => {
    setTempToken(token);
    setTempTokenExpiresAt(Date.now() + STEP_UP_TOKEN_TTL_MS);
    await loadQr(token);
  };

  const handleRetry = () => {
    const needsNewToken =
      isReconfigure && !resetDone && (!tempToken || Date.now() >= tempTokenExpiresAt);
    if (needsNewToken) {
      setTempToken(null);
      setLoadError(null);
      setVerifyNotice(t("reconfigureDialog.verificationExpired"));
      setStep("verify");
      return;
    }
    void loadQr(tempToken);
  };

  const handleSetupConfirmed = () => {
    toast.success(t(`${copyKey}.successToast`));
    onAuthenticatorChanged();
    reset();
    onOpenChange(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    if (step === "currentPassword") void handleCurrentPasswordNext();
    else if (step === "loadFailed") handleRetry();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-1rem)] overflow-y-auto rounded-xl sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">{t(`${copyKey}.title`)}</DialogTitle>
          <DialogDescription>
            {t("stepOf", { step: stepNumbers[step], total: totalSteps })}
            {" · "}
            {step === "currentPassword" || (!isReconfigure && step === "loadFailed")
              ? t("reconfigureDialog.stepTitles.currentPassword")
              : step === "authenticatorSetup"
                ? t("reconfigureDialog.stepTitles.authenticatorSetup")
                : t("reconfigureDialog.stepTitles.verify")}
          </DialogDescription>
        </DialogHeader>

        {step === "authenticatorSetup" && qrCode ? (
          <>
            {isReconfigure && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
                {t("reconfigureDialog.oldAuthenticatorRemoved")}
              </p>
            )}
            <AuthenticatorSetupStep
              idPrefix={`${mode}-authenticator`}
              qrCode={qrCode}
              onConfirmed={handleSetupConfirmed}
              onBack={() => handleOpenChange(false)}
              backLabel={t("reconfigureDialog.cancel")}
            />
          </>
        ) : step === "verify" ? (
          <VerifyIdentityStep
            purpose={AUTH_FLOW_PURPOSE_RESET_TOTP}
            email={email}
            totpEnabled
            defaultMethod={OTP_PREFERENCE_EMAIL}
            onlyMethod={OTP_PREFERENCE_EMAIL}
            notice={verifyNotice}
            idPrefix={`${mode}-authenticator`}
            onVerified={handleVerified}
            onBack={() => setStep("currentPassword")}
          />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {step === "currentPassword" && (
              <>
                <p className="text-sm text-gray-500">{t(`${copyKey}.currentPasswordHint`)}</p>
                <PasswordField
                  id={`${mode}-authenticator-current-password`}
                  label={t("reconfigureDialog.currentPasswordLabel")}
                  value={currentPassword}
                  onChange={(value) => {
                    setCurrentPassword(value);
                    setPasswordError(null);
                  }}
                  error={passwordError}
                  disabled={loading}
                  autoFocus
                  autoComplete="current-password"
                />
              </>
            )}

            {step === "loadFailed" && loadError && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{loadError}</p>
            )}

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                className="cursor-pointer"
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={loading}
              >
                {t("reconfigureDialog.cancel")}
              </Button>
              <Button className="cursor-pointer" type="submit" disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {step === "currentPassword"
                  ? t("reconfigureDialog.next")
                  : t("reconfigureDialog.retry")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
